import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Button,
  CopyButton,
  DSButtonType,
  EmptyContent,
  HeadItemType,
  Icon,
  LoadingHavaianas,
  Pagination,
  SlimButton,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'components'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { useDispatch, useSelector } from 'store/store'
import { useParams } from 'react-router-dom'
import {
  SalesOrderOvCancelOperation,
  SalesOrderOvDeleteOperation,
  SalesOrderSapOperation,
  SendSapOperation,
  asyncGetSalesOrderOvs,
  setOvsList
} from 'store/SalesOrderContext/salesOrderSlice'
import { getReasonsRefusal } from 'store/OVContext/ovSlice'
import { SalesOrderOvItem } from 'store/SalesOrderContext/types/ovsList'
import { theme } from 'styles/theme/hav-theme'
import { URL_CONST } from 'constants/urls'
import OvListMenu from './components/OvListMenu'
import CancelOvForm, { CancelOvReason } from './components/CancelOvForm'
import { ActionPerm } from 'components/Structures/Header/constants'
import { usePermissions } from 'commons/customHooks/useAuth'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { OvToCancelData, OvToSendData } from 'store/OVContext/ovContextTypes'
import JustifyNDForm, { JustifyNDFields } from './components/JustifyNDForm'
import { toBase64 } from 'commons/utils/object'

import * as S from './styled'
import { OVTypes, StatusNdType } from 'constants/enums'
import { getEDIRowIcon } from 'container/ManageOrders/utils'

type ActiveActionsStatus = {
  cancel: boolean
  delete: boolean
  return: boolean
  send: boolean
  conflict: boolean
  yrba: boolean
}

type ActionButtonProps = {
  enabled: boolean
  tooltip: React.ReactChild
  button: React.ReactNode
}

const OvsTab = () => {
  const dispatch = useDispatch()
  const { orderNumber } = useParams()
  const {
    draftBody: { tp_orig: orderOrigin },
    ovsListData,
    ovsListData: {
      OrdensVenda: ovsList,
      RowsCount: totalItems,
      ValorTotalOrdensVenda: totalValue
    },
    isLoadingOvsList: loading
  } = useSelector((state) => state.salesOrder)

  // NOTE: Any change in order of columns or add/remove columns. Should be updated colunsNotNavigate: [8]
  const tableHead: HeadItemType[] = [
    { label: 'Status', columnName: 'Status' },
    { label: 'OV', columnName: 'OV' },
    { label: 'OV SAP', columnName: 'OVSAP' },
    { label: 'Fábrica', columnName: 'Fabrica' },
    { label: 'Prev. faturam.', columnName: 'PrevFaturam' },
    { label: 'Prev. entrega', columnName: 'PrevEntrega' },
    { label: 'Volume', columnName: 'Volume' },
    { label: 'Valor (R$)', columnName: 'Valor' },
    { label: '', columnName: '' }
  ]

  const getOvStatusColor = (statusNumber: number) => {
    // TODO: Refactor this, to improve legibility of status colors
    const pending = [0, 5, 8, 20, 21, 22],
      approval = [9, 10, 15],
      processing = [30, 45, 50],
      sap = [40],
      reserve = [60],
      shipment = [65, 70],
      invoicing = [75],
      invoiced = [80],
      canceled = [90]

    if (pending.includes(statusNumber)) return theme.colors.alert.danger.dark
    if (approval.includes(statusNumber)) return theme.colors.alert.warning.dark
    if (processing.includes(statusNumber)) return theme.colors.alert.info.dark
    if (sap.includes(statusNumber)) return theme.colors.support.second
    if (reserve.includes(statusNumber)) return theme.colors.support.third
    if (shipment.includes(statusNumber)) return theme.colors.support.fourth
    if (invoicing.includes(statusNumber)) return theme.colors.support.first
    if (invoiced.includes(statusNumber)) return theme.colors.alert.success.dark
    if (canceled.includes(statusNumber)) return theme.colors.neutral.light
  }

  const PAGE_SIZE = 15
  const defaultActionsStatus = {
    cancel: false,
    delete: false,
    return: false,
    send: false,
    conflict: false,
    yrba: false
  }

  useEffect(() => {
    setActiveActions((old) => ({
      ...old,
      yrba: ovsList.some((ov) => ov.TipoOrdemVenda === OVTypes.YRBA)
    }))
  }, [ovsList])

  const [activeActions, setActiveActions] =
    useState<ActiveActionsStatus>(defaultActionsStatus)
  const [currentPage, setCurrentPage] = useState(1)
  const [numberOfPages, setNumberOfPages] = useState(0)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [ovsToDelete, setOvsToDelete] = useState<string[]>([])
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [ovsToCancel, setOvsToCancel] = useState<OvToCancelData[]>([])
  const [openSendModal, setOpenSendModal] = useState(false)
  const [ovsToSend, setOvsToSend] = useState<OvToSendData[]>([])

  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.OV, action)
  }

  const hasUnjustifiedND = useMemo(
    () =>
      ovsList
        .filter((item) => item.checked)
        .some((ov) => ov.NegociacaoDiferenciada && !ov.DataJustificativaNegociacao),
    [ovsList]
  )

  const getTableData = () => {
    const tableData = []

    if (ovsList.length) {
      const ovsData = ovsList.map((ov) => {
        const id = ov.NumeroOrdemVenda
        return {
          id,
          disableCheckbox: ov.TipoOrdemVenda === OVTypes.YRBA,
          checked: ov.checked,
          linkNavigate: `${URL_CONST.MANAGE_OV}/${ov.NumeroOrdemVenda}`,
          colunsNotNavigate: [8],
          content: [
            <Tag
              key={`status-${id}`}
              tooltipLabel={ov.StatusDescription}
              background={getOvStatusColor(+ov.StatusNumber)}
            >
              {ov.StatusNumber}
            </Tag>,
            <S.OrderNumber key={`number-${id}`}>
              <span>{ov.NumeroOrdemVenda}</span>
              <CopyButton text={ov.NumeroOrdemVenda} />
              {renderNDIcon(ov)}
              {getEDIRowIcon(orderOrigin, undefined, true)}
            </S.OrderNumber>,
            <S.OrderNumberSAP key={`number-ov-sap-${id}`}>
              <span>{ov.NumeroOrdemVendaSap}</span>
              {ov.NumeroOrdemVendaSap && <CopyButton text={ov.NumeroOrdemVendaSap} />}
            </S.OrderNumberSAP>,
            ov.CodigoFabrica,
            ov.DataFaturamentoPrevisto,
            ov.DataEntregaCalculada,
            numberToDecimalString(ov.Volume),
            formatToPTBRNumber(ov.ValorTotalOrdemVenda),
            <OvListMenu
              key={`menu-${id}`}
              ov={ov}
              handleSap={handleSapOperation}
              handleSendSap={handleSendSap}
              handleDelete={handleDeleteAction}
              handleCancel={handleCancelAction}
            />
          ]
        }
      })

      tableData.push(...ovsData)
    }

    return tableData
  }

  const handleActions = useCallback(
    (updatedItems: SalesOrderOvItem[]) => {
      const checkedItems = updatedItems.filter((item) => item.checked)

      if (!checkedItems.length) {
        setActiveActions(defaultActionsStatus)
        return
      }

      const isCancelEnabled = checkedItems.every((item) => {
        return +item.StatusNumber >= 0 && +item.StatusNumber <= 8
      })

      const isDeleteEnabled = checkedItems.every((item) => {
        return +item.StatusNumber === 0 && !item.NumeroOrdemVendaSap
      })

      const isReturnEnabled = checkedItems.every((item) => {
        // TODO: Refactor this, to improve legibility of status numbers
        const returnStatus = [9, 10, 15, 20, 21, 22, 40, 45]
        return returnStatus.includes(+item.StatusNumber)
      })

      const isSendEnabled = checkedItems.every((item) => {
        return +item.StatusNumber >= 0 && +item.StatusNumber <= 8
      })

      const isConflict =
        checkedItems.some((item) => {
          return +item.StatusNumber <= 8
        }) &&
        checkedItems.some((item) => {
          return +item.StatusNumber > 8
        })

      setActiveActions((old) => ({
        ...old,
        cancel: isCancelEnabled,
        delete: isDeleteEnabled,
        return: isReturnEnabled,
        send: isSendEnabled,
        conflict: isConflict
      }))
    },
    [ovsList]
  )

  const setCheckedExclusive = (isChecked: boolean, id: string) => {
    const updatedItems = ovsList.map((item) =>
      id === item.NumeroOrdemVenda
        ? { ...item, checked: isChecked }
        : { ...item, checked: false }
    )
    dispatch(setOvsList({ ...ovsListData, OrdensVenda: updatedItems }))
    handleActions(updatedItems)
  }

  const toggleCheckedItem = (isChecked: boolean, id?: string) => {
    if (id) {
      const updatedItems = ovsList.map((item) =>
        id === item.NumeroOrdemVenda ? { ...item, checked: isChecked } : item
      )
      dispatch(setOvsList({ ...ovsListData, OrdensVenda: updatedItems }))
      handleActions(updatedItems)
    } else {
      const updatedItems = ovsList.map((item) => ({
        ...item,
        checked: isChecked
      }))

      dispatch(setOvsList({ ...ovsListData, OrdensVenda: updatedItems }))
      handleActions(updatedItems)
    }
  }

  const handleDeleteAction = (item?: SalesOrderOvItem) => {
    const checkedItems = ovsList.filter((item) => item.checked)
    const ovNumbers = item
      ? [item.NumeroOrdemVenda]
      : checkedItems.map((item) => item.NumeroOrdemVenda)

    setOpenDeleteModal(true)
    setOvsToDelete(ovNumbers)
  }

  const dispatchDeleteOperation = () => {
    dispatch(
      SalesOrderOvDeleteOperation(
        ovsToDelete,
        { paginas: 0, linhas: PAGE_SIZE },
        orderNumber ?? ''
      )
    )
    toggleCheckedItem(false)
    setOpenDeleteModal(false)
  }

  const handleCancelAction = (item?: SalesOrderOvItem) => {
    const checkedItems = ovsList.filter((item) => item.checked)
    const ovNumbers = item
      ? [{ numeroOrdem: item.NumeroOrdemVenda, dataUltimaAlteracao: item.DataAlteracao }]
      : checkedItems.map((item) => {
          return {
            numeroOrdem: item.NumeroOrdemVenda,
            dataUltimaAlteracao: item.DataAlteracao
          }
        })

    dispatch(getReasonsRefusal())
    setOpenCancelModal(true)
    setOvsToCancel(ovNumbers)
  }

  const dispatchCancelOperation = (data: CancelOvReason) => {
    const body = {
      ordensVenda: ovsToCancel,
      codigoMotivo: data.reasonsRefusal,
      justificativa: data.refusalDescription
    }
    dispatch(
      SalesOrderOvCancelOperation(
        body,
        { paginas: 0, linhas: PAGE_SIZE },
        orderNumber ?? ''
      )
    )
    toggleCheckedItem(false)
    setOpenCancelModal(false)
  }

  const handleSapOperation = (operation: string, item?: SalesOrderOvItem) => {
    const checkedItems = ovsList.filter((item) => item.checked)
    const body = item
      ? [{ numeroOrdemVenda: item.NumeroOrdemVenda, dataAlteracao: item.DataAlteracao }]
      : checkedItems.map((item) => ({
          numeroOrdemVenda: item.NumeroOrdemVenda,
          dataAlteracao: item.DataAlteracao
        }))

    dispatch(
      SalesOrderSapOperation(
        operation,
        body,
        { paginas: 0, linhas: PAGE_SIZE },
        orderNumber ?? ''
      )
    )
    toggleCheckedItem(false)
  }

  const handleSendSap = (item?: SalesOrderOvItem) => {
    const checkedItems = ovsList.filter((item) => item.checked)
    const ovs = item
      ? [{ NumeroOrdemVenda: item.NumeroOrdemVenda, DataAlteracao: item.DataAlteracao }]
      : checkedItems.map((item) => ({
          NumeroOrdemVenda: item.NumeroOrdemVenda,
          DataAlteracao: item.DataAlteracao
        }))

    if (item) {
      setCheckedExclusive(true, item.NumeroOrdemVenda)
    }
    setOvsToSend(ovs)
    setOpenSendModal(true)
  }

  const dispatchSendSap = async (data?: JustifyNDFields) => {
    let body = { ListaOvs: ovsToSend }
    if (data) {
      const fileString = (await toBase64(data.file)) as string
      body = {
        ...body,
        ...{
          CodigoMotivoNegociacao: data.codReason,
          NegociacaoDiferenciada: data.descReason,
          JustificativaNegociacaoDiferenciada: data.descRequest,
          NomeAnexo: data.file_name,
          Anexo: fileString.split(',')[1]
        }
      }
    }
    dispatch(SendSapOperation(body, { paginas: 0, linhas: PAGE_SIZE }, orderNumber ?? ''))
    toggleCheckedItem(false)
    setOpenSendModal(false)
  }

  const NDIcons: Record<StatusNdType, { title: string; color: string }> = {
    approved: {
      title: 'Justificativa aprovada',
      color: theme.colors.alert.success.dark
    },
    pending: {
      title: 'Justificativa pendente',
      color: theme.colors.alert.danger.dark
    },
    justified: {
      title: 'Justificado',
      color: theme.colors.neutral.light
    }
  }

  const renderNDIcon = (ov: SalesOrderOvItem) => {
    const ndStatus = (): StatusNdType => {
      if (ov.NegociacaocaoAprovada) return 'approved'
      if (ov.DataJustificativaNegociacao) return 'justified'
      return 'pending'
    }

    if (!ov.NegociacaoDiferenciada) return <></>

    return (
      <Tooltip title={NDIcons[ndStatus()].title}>
        <span>
          <Icon icon="nd" color={NDIcons[ndStatus()].color} />
        </span>
      </Tooltip>
    )
  }

  useEffect(() => {
    dispatch(
      asyncGetSalesOrderOvs(
        { paginas: currentPage - 1, linhas: PAGE_SIZE },
        orderNumber ?? ''
      )
    )
  }, [currentPage])

  useEffect(() => {
    setNumberOfPages(Math.ceil(totalItems / PAGE_SIZE))
  }, [ovsList])

  const ActionButton = ({ enabled, tooltip, button }: ActionButtonProps) => {
    return (
      <>
        {enabled ? (
          button
        ) : (
          <Tooltip title={tooltip} arrow placement="top">
            <div>{button}</div>
          </Tooltip>
        )}
      </>
    )
  }

  return (
    <S.Wrapper>
      <Typography design="hav-default-03-text">
        Nesta página, você pode visualizar e acessar as ordens de venda geradas a partir
        deste pedido. Também é possível executar algumas ações principais, como envio e
        retorno do SAP, ou cancelamento e exclusão de uma OV.
      </Typography>

      {loading ? (
        <S.LoadingContainer>
          <LoadingHavaianas />
        </S.LoadingContainer>
      ) : (
        <>
          {!ovsList.length ? (
            <EmptyContent
              title="Sem OVs geradas"
              description={
                <>
                  Ao solicitar os materiais e gerar OVs você poderá visualizá-las aqui.
                  <br />É possível fazer a solicitação através da aba “Em aberto”.
                </>
              }
            />
          ) : (
            <S.Container>
              <S.TableWrapper>
                <Table
                  hasCheckbox
                  setChecked={toggleCheckedItem}
                  rowHeight={32}
                  iconColumns={[0]}
                  customAlign={[
                    { colIndex: 0, textAlign: 'center' },
                    { colIndex: 8, textAlign: 'right' }
                  ]}
                  headData={tableHead}
                  bodyData={getTableData()}
                />
              </S.TableWrapper>
              <S.SummaryWrapper>
                <div>
                  {totalItems > PAGE_SIZE && (
                    <Pagination
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      numberOfPages={numberOfPages}
                      numberOfItems={totalItems}
                      itemsPerPage={PAGE_SIZE}
                    />
                  )}
                </div>
                <S.SummaryRow>
                  <S.SummaryItem>
                    <Typography design="hav-default-02-text">
                      OVs no pedido{activeActions.yrba}
                    </Typography>
                    <span>{numberToDecimalString(totalItems)}</span>
                  </S.SummaryItem>
                  <S.SummaryItem>
                    <Typography design="hav-default-02-text">Valor em OVs</Typography>
                    <span>{formatToPTBRNumber(totalValue)}</span>
                  </S.SummaryItem>
                </S.SummaryRow>
              </S.SummaryWrapper>
              <S.ActionsRow>
                <ActionButton
                  enabled={activeActions.cancel}
                  tooltip={
                    <>
                      Apenas os status entre 00 e 08
                      <br />
                      podem ser cancelados
                    </>
                  }
                  button={
                    <SlimButton
                      id="btn-cancel"
                      variant="secondary"
                      content="Cancelar"
                      type="button"
                      icon={<Icon icon="cancel" size="15" />}
                      disabled={
                        !activeActions.cancel || shouldDisable(ACTIONS_SCREENS.CANCEL)
                      }
                      onClick={() => handleCancelAction()}
                    />
                  }
                />
                <ActionButton
                  enabled={activeActions.delete}
                  tooltip={
                    <>
                      Apenas Ordens de Venda sem o número <br />
                      de OV SAP podem ser excluídas
                    </>
                  }
                  button={
                    <SlimButton
                      id="btn-delete"
                      variant="secondary"
                      content="Excluir"
                      type="button"
                      icon={<Icon icon="trash" size="15" />}
                      disabled={
                        !activeActions.delete || shouldDisable(ACTIONS_SCREENS.DELETE)
                      }
                      onClick={() => handleDeleteAction()}
                    />
                  }
                />
                <ActionButton
                  enabled={activeActions.return}
                  tooltip={
                    <>
                      Apenas os status 09, 15, 20, 21, 22, <br />
                      40 e 45 podem retornar
                    </>
                  }
                  button={
                    <SlimButton
                      id="btn-return"
                      variant="secondary"
                      content="Retornar"
                      type="button"
                      icon={<Icon icon="undo" size="15" />}
                      disabled={
                        !activeActions.return || shouldDisable(ACTIONS_SCREENS.RETURN)
                      }
                      onClick={() => handleSapOperation('retornar')}
                    />
                  }
                />
                <ActionButton
                  enabled={activeActions.send}
                  tooltip={
                    <>
                      {activeActions.conflict ? (
                        <>
                          Sua seleção inclui OVs com status conflitantes. Selecione OVs
                          com status compatíveis para realizar ações em massa.
                        </>
                      ) : (
                        <>
                          {activeActions.yrba ? (
                            <>
                              Não é possível fazer o envio massivo de
                              <br /> OVs do tipo YRBA.
                            </>
                          ) : (
                            <>
                              {activeActions.yrba} Apenas os status entre 00 e 08 podem
                              <br />
                              ser enviados
                            </>
                          )}
                        </>
                      )}
                    </>
                  }
                  button={
                    <SlimButton
                      id="btn-send"
                      variant="secondary"
                      content="Enviar"
                      type="button"
                      icon={<Icon icon="send" size="15" />}
                      disabled={
                        !activeActions.send || shouldDisable(ACTIONS_SCREENS.SEND)
                      }
                      onClick={() => handleSendSap()}
                    />
                  }
                />
              </S.ActionsRow>
            </S.Container>
          )}
        </>
      )}

      {openDeleteModal && (
        <ModalContentLayout
          closeAction={() => setOpenDeleteModal(false)}
          title="Excluir"
          description={`Esta ação excluirá os itens inclusos ${
            ovsToDelete.length > 1 ? 'nestas OVs' : 'nesta OV'
          } e eles serão removidos do
          seu pedido.`}
        >
          <S.Footer>
            <div>
              <Button
                content="Voltar"
                type="button"
                color="secondary"
                design={DSButtonType.ICON_BUTTON}
                onClick={() => setOpenDeleteModal(false)}
              />
            </div>
            <div>
              <Button
                content="Excluir"
                color="primary"
                design={DSButtonType.PRIMARY_BUTTON}
                onClick={dispatchDeleteOperation}
              />
            </div>
          </S.Footer>
        </ModalContentLayout>
      )}

      {openCancelModal && (
        <ModalContentLayout
          closeAction={() => setOpenDeleteModal(false)}
          title="Cancelar"
          description={`Deste modo os itens inclusos ${
            ovsToDelete.length > 1 ? 'nestas Ordens de venda' : 'nesta Ordem de venda'
          } serão removidos do pedido. Deseja cancelar ${
            ovsToDelete.length > 1 ? 'estas OVs' : 'essa OV'
          }?`}
        >
          <CancelOvForm
            setOpen={setOpenCancelModal}
            cancelAction={dispatchCancelOperation}
          />
        </ModalContentLayout>
      )}

      {openSendModal && (
        <ModalContentLayout
          closeAction={() => setOpenSendModal(false)}
          title="Enviar OVs"
          width={hasUnjustifiedND ? 580 : 430}
          closeButton
          titleSize={20}
          description="Ao enviar a fábrica dará continuidade à sua Ordem de Venda (OV) e os materiais só poderão ser editados novamente caso solicite o Retorno."
        >
          {hasUnjustifiedND ? (
            <JustifyNDForm setOpen={setOpenSendModal} sendAction={dispatchSendSap} />
          ) : (
            <S.Footer>
              <SlimButton
                content="Voltar"
                type="button"
                variant="secondary"
                onClick={() => setOpenSendModal(false)}
              />
              <SlimButton
                content="Enviar"
                variant="primary"
                onClick={() => dispatchSendSap()}
              />
            </S.Footer>
          )}
        </ModalContentLayout>
      )}
    </S.Wrapper>
  )
}

export default OvsTab
